import React from 'react'
import ProgressItem from './ProgressItem'

type BaseItemStatus = {
	id: string
	name?: string
	status:
		| 'pending'
		| 'uploading'
		| 'processing'
		| 'completed'
		| 'failed'
		| 'canceled'
	error?: string
	progress?: number
	type?: number
}

export interface ProgressItemsListProps<T extends BaseItemStatus> {
	items: T[]
	renderCustomContent?: (item: T, index: number) => React.ReactNode
	renderActions?: (item: T, index: number) => React.ReactNode
	showProgressBar?: boolean
	filterTechnicalErrors?: boolean
	getItemKey?: (item: T, index: number) => string
	getDisplayName?: (item: T) => string
	isFolder?: (item: T) => boolean
}

const ProgressItemsList = <T extends BaseItemStatus>({
	items,
	renderCustomContent,
	renderActions,
	showProgressBar = false,
	filterTechnicalErrors = false,
	getItemKey,
	getDisplayName,
	isFolder,
}: ProgressItemsListProps<T>) => {
	const defaultGetItemKey = (item: T, index: number) => `${item.id}_${index}`
	const defaultGetDisplayName = (item: T) => item.name || ''
	const defaultIsFolder = (item: T) => item.type === 1

	const itemKey = getItemKey || defaultGetItemKey
	const displayName = getDisplayName || defaultGetDisplayName
	const checkIsFolder = isFolder || defaultIsFolder

	return (
		<div className="flex flex-col mt-3 justify-start items-start gap-1 w-full min-h-28 max-h-28 lg:min-h-56 lg:max-h-56 overflow-y-auto">
			{items.map((item, index) => {
				const isLoading =
					item.status === 'pending' ||
					item.status === 'uploading' ||
					item.status === 'processing'
				const itemIsFolder = checkIsFolder(item)
				const itemDisplayName = displayName(item)

				return (
					<ProgressItem
						key={itemKey(item, index)}
						item={item}
						index={index}
						itemKey={itemKey(item, index)}
						displayName={itemDisplayName}
						isFolder={itemIsFolder}
						isLoading={isLoading}
						showProgressBar={showProgressBar}
						filterTechnicalErrors={filterTechnicalErrors}
						renderCustomContent={renderCustomContent}
						renderActions={renderActions}
					/>
				)
			})}
		</div>
	)
}

export default ProgressItemsList
